import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { I18nProvider } from '@lingui/react';

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

import './styles.css';
import reportWebVitals from './reportWebVitals.ts';
import { TooltipProvider } from '@/components/ui/tooltip.tsx';
import { RouteError } from '@/components/route-error.tsx';
import { NotFound } from '@/components/not-found.tsx';
import { PageLoadingSkeleton } from '@/components/page-loading-skeleton.tsx';
import { isExpectedError, setupApiInterceptors } from '@/lib/api.ts';
import { i18n, activateLocale } from '@/lib/i18n.ts';
import { pickInitialLocale } from '@/lib/locale-detection.ts';

// Create a new router instance
//
// If you are trying to access these two directly, there is a better way to do your thingy.

const TanStackQueryProviderContext = TanStackQueryProvider.getContext();
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: NotFound,
  // Router-wide loading feedback: any route whose loader/beforeLoad stays pending
  // past defaultPendingMs paints the generic skeleton in its Outlet (parent layout
  // stays mounted) instead of freezing on the previous page. A cached navigation
  // resolves under the threshold and shows nothing. A route can still override
  // with its own pendingComponent.
  defaultPendingComponent: PageLoadingSkeleton,
  defaultPendingMs: 150,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Sentry — only initialize when a DSN is provided (set per-mode via
// VITE_SENTRY_DSN; absent from the dev `.env`, so local stays a no-op and never reports).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE, // development, production or --mode
    // Central net: drop expected/operational errors (auth, network/offline, 5xx)
    // that reach any Sentry handler — global, boundary, or unhandled rejection.
    beforeSend: (event, hint) => (isExpectedError(hint.originalException) ? null : event),
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
      // userInfo: false,
      // httpBodies: []
    },
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        // If you need to customize masking, ignoring and blocking in replays, check the docs.
        // https://docs.sentry.io/platforms/javascript/session-replay/privacy/
        // There are special mask/ignore/block CSS selector and class attributes that can be used without change here.
        maskAllText: false,
        // type="password" are always protected even with this override
        maskAllInputs: false,
        // blockAllMedia: false,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Filter Spans
    ignoreSpans: [
      // OpenStreetMap tiles
      // {op: /^resource/, name: /openstreetmap\.org/},
      // Resource fetch for images, CSS, script, png/icons, stripe iframe
      { op: /^resource.(img|css|script|other|iframe)$/ },
    ],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.barracuda\.rossinienergy\.com\//,
      /^https:\/\/api\.charge\.re\//,
    ],
    // Session Replay
    replaysSessionSampleRate: 0, // Disable it, too costly
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true,
  });
}

// Redirect to /login whenever a request fails because the session is gone.
setupApiInterceptors(router);

// Activate the detected/persisted locale before the first render so non-English
// catalogs are loaded up front (no untranslated flash).
await activateLocale(pickInitialLocale());

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <I18nProvider i18n={i18n}>
        <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
          <TooltipProvider>
            <RouterProvider router={router} />
          </TooltipProvider>
        </TanStackQueryProvider.Provider>
      </I18nProvider>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
